import * as React from 'react';

import {NothingFoundEmptyState} from 'shells/empty-states/nothing-found-empty-state';
import {ErrorState} from 'shells/error-state/error-state';
import {LoadingPage} from 'shells/loading-page/loading-page';
import {DisplayOnlyTable} from 'shells/table/display-only-table';

import type {LandingPageStubFragment as Page} from 'nutshell-graphql-types';

import {EmptyState} from './empty-state';

type Props = {
    landingPages: Page[];
    isLoading: boolean;
    error: string | null | undefined;
    archived: boolean;
};

export function LandingPagesTable(props: Props) {
    const {landingPages} = props;

    const columns = React.useMemo(
        () => [
            {
                id: 'name',
                Header: 'Page name',
                accessor: (page: Page) => page,
                dataType: 'linkedName',
                hasLeftBorder: true,
            },
            {
                id: 'numViews',
                Header: '# of views',
                minWidth: 100,
                accessor: (page: Page) => page.numViews,
                dataType: 'integer',
            },
            {
                id: 'publishedUrl',
                Header: 'URL',
                accessor: (page: Page) => page.customPublishedUrl || page.nutshellPublishedUrl,
                width: 150,
                minWidth: 150,
                shouldTruncate: true,
                dataType: 'string',
            },
            {
                id: 'createdTime',
                Header: 'Date created',
                accessor: (page: Page) => page.createdTime,
                dataType: 'timestamp',
            },
            {
                id: 'lastModifiedTime',
                Header: 'Last modified',
                accessor: (page: Page) => page.lastModifiedTime,
                dataType: 'timestamp',
            },
            {
                id: 'lastModifiedBy',
                Header: 'Last modified by',
                accessor: (page: Page) =>
                    page.publishedVersion && page.publishedVersion.publisherUser,
                dataType: 'linkedEntity',
                hasRightBorder: true,
            },
        ],
        []
    );

    return (
        <div>
            <DisplayOnlyTable
                // @ts-expect-error
                columns={columns}
                data={landingPages}
                fullWidth={true}
                error={props.error}
                isLoading={props.isLoading}
                disableSorting={false}
                specialStates={[
                    {
                        shouldRender: () => Boolean(props.error),
                        component: <ErrorState errorMessage={props.error} />,
                    },
                    {
                        shouldRender: () => props.isLoading,
                        component: <LoadingPage padding={0} />,
                    },
                    {
                        shouldRender: () =>
                            Boolean(
                                props.landingPages &&
                                    !props.landingPages.length &&
                                    !props.isLoading &&
                                    props.archived
                            ),
                        component: (
                            <NothingFoundEmptyState
                                title='No archived pages were found'
                                subtitle='Your archived pages will appear here.'
                            />
                        ),
                    },
                    {
                        shouldRender: () => !props.landingPages.length && !props.isLoading,
                        component: <EmptyState />,
                    },
                ]}
            />
        </div>
    );
}
